import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Collapse,
  InputAdornment,
  Stack,
  Alert,
  AlertTitle,
  Tooltip,
} from "@mui/material";
import {
  Search,
  FilterList,
  ExpandMore,
  ExpandLess,
  MonetizationOn,
  People,
  Person,
  TrendingUp,
  Close,
  Tune,
} from "@mui/icons-material";
import typography from "../../cssUtils/typography";
import ButtonTwo from "../../common/dashboard-button";
import TableSearchInput from "../TableSearchInput";
import AppTable from "../../common/app-table";

const initialData = {
  totalAUM: "₹0",
  aumTrend: "+0%",
  totalClients: 0,
  totalRM: 0,
  activeRM: 0,
};

const initialRMList = {
  count: 0,
  results: [],
};

const URL =
  process.env.NODE_ENV === "production" ? "api" : `http://localhost:8000/api`;
// const srm_id = 2;

const RMMetricsDashboardTab = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [charCount, setCharCount] = useState(0);
  const [page, setPage] = useState(0);
  const [dashboardData, setDashboardData] = useState(initialData);
  const [rmListData, setRmListData] = useState(initialRMList);
  const [loading, setLoading] = useState(false);
  const [alertObj, setAlertObj] = useState({
    active: false,
    msg: "",
    type: "",
    statusCode: "",
  });
  const [expandedCards, setExpandedCards] = useState({
    metrics: true,
    rmList: true,
  });
   
  const tableColumns = [
    { id: "name",        label: "RM Name"       },
    { id: "aum",         label: "AUM"           },
    { id: "clientCount", label: "Client Count"  },
    { id: "region",      label: "Region"        },
    {
      id: "lastActivity",
      label: "Last Activity",
      render: (rm) =>
        rm.lastActivity ? new Date(rm.lastActivity).toLocaleDateString() : "No activity",
    },
    {
      id: "status",
      label: "Status",
      render: (rm) => (
        <Chip
          label={rm.status.charAt(0).toUpperCase() + rm.status.slice(1)}
          color={rm.status === "active" ? "success" : "error"}
          size="small"
        />
      ),
    },
  ];  
  const formatAUM = (aumValue) => {
    const num = parseFloat(aumValue);
    if (isNaN(num)) return "₹0.00";
    const [integerPart, decimalPart] = num.toFixed(2).split(".");
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return `₹${formattedInteger}.${decimalPart}`;
  };

  // Function to fetch dashboard metrics
  const fetchRMData = async () => {
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const tenant = localStorage.getItem("tenant");
      const headers = {
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };
      const response = await axios.get(
        `${URL}/rm-metrics/metrics/?srm_id=${user.id}`,
        {
          headers: {
            "Content-Type": "application/json", // Optional, depending on API requirements
            "X-Tenant-Id": tenant,
          },
        },
      );

      if (response.data) {
        setDashboardData((prevData) => ({
          ...prevData,
          totalAUM: formatAUM(response.data.totalAUM || "0"),
          totalClients: response.data.totalClients || 0,
          totalRM: response.data.totalRM || 0,
          activeRM: response.data.totalRM || 0,
          aumTrend: response.data.aumTrend || prevData.aumTrend,
        }));
      }
    } catch (err) {
      console.error("Error fetching metrics data:", err);
    }
  };

  // Function to fetch RM list with pagination
  const fetchRMListData = async (search = "", pageNum = 0) => {
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const tenant = localStorage.getItem("tenant");
      const headers = {
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };
      const response = await axios.get(
        `${URL}/rm-metrics/srm-rm-list/?srm_id=${user.id}&search_value=${search}&limit=10&offset=${pageNum * 10}`,
        { headers: headers },
      );

      if (response.data) {
        setRmListData({
          count: response.data.count,
          results: response.data.results.map((rm) => ({
            id: rm.id,
            name: rm.rm_name,
            aum: formatAUM(rm.aum),
            clientCount: rm.client_count,
            region: rm.region,
            lastActivity: rm.last_activity,
            status: rm.status.toLowerCase(),
          })),
        });
      }
    } catch (err) {
      console.error("Error fetching RM list:", err);
      setRmListData(initialRMList);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRMData();
    fetchRMListData();
  }, []);

  const handleSearchChange = (event) => {
    const newSearchTerm = event.target.value;
    setSearchTerm(newSearchTerm);
    setCharCount(newSearchTerm.length);
    setPage(0);
    fetchRMListData(newSearchTerm, 0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchRMListData(searchTerm, newPage);
  };

  const handleAlertClose = () => {
    setAlertObj((prev) => ({ ...prev, active: false }));
  };

  return (
    <Box sx={{ p: 0, pt: { xs: 2, md: 4 }, maxWidth: "xl", margin: "auto" }}>
      {/* Dashboard Header */}

      {/* Metrics Cards Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Total AUM Card */}
        <Grid item xs={12} sm={6}>
          <Card>
            <CardHeader
              title="Total AUM"
              action={
                <IconButton>
                  <MonetizationOn color="primary" />
                </IconButton>
              }
              sx={{ ...typography.h1}}
            />
            <CardContent>
              <Typography variant="h4" sx={{ ...typography.h1}}>{dashboardData.totalAUM}</Typography>
              <Typography
                variant="subtitle2"
                color="success.main"
                sx={{ display: "flex", alignItems: "center", gap: 1, ...typography.bodyLarge }}
              >
                <TrendingUp fontSize="small" />
                {dashboardData.aumTrend}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Total Clients Card */}
        <Grid item xs={12} sm={6}>
          <Card>
            <CardHeader
              title="Total Clients"
              action={
                <IconButton>
                  <People color="primary" />
                </IconButton>
              }
              sx={{ ...typography.h1}}
            />
            <CardContent>
              <Typography variant="h4" sx={{ ...typography.h1}}>{dashboardData.totalClients}</Typography>
              <Typography variant="subtitle2" color="text.secondary" sx={{ ...typography.bodyLarge}}>
                Across all relationship managers
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Total RMs Card */}
        <Grid item xs={12} sm={6}>
          <Card>
            <CardHeader
              title="Total RMs"
              action={
                <IconButton>
                  <Person color="primary" />
                </IconButton>
              }
              sx={{ ...typography.h1}}
            />
            <CardContent>
              <Typography variant="h4" sx={{ ...typography.h1}}>{dashboardData.totalRM}</Typography>
              <Typography variant="subtitle2" color="text.secondary" sx={{ ...typography.bodyLarge}}>
                {dashboardData.activeRM} Active RMs
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Average Performance Card */}
        <Grid item xs={12} sm={6}>
          <Card>
            <CardHeader
              title="Average Performance"
              action={
                <IconButton>
                  <TrendingUp color="primary" />
                </IconButton>
              }
              sx={{ ...typography.h1}}
            />
            <CardContent>
              <Typography variant="h4" sx={{ ...typography.h1}}>N/A%</Typography>
              <Typography variant="subtitle2" color="text.secondary" sx={{ ...typography.bodyLarge}}>
                Target Achievement Rate
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* RM List Section */}
      <Box mb={2}>
        <Typography sx={{ ...typography.h1 }} mb={2}>
          Relationship Manager List
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
          <Tooltip title="Search relationship managers by name, region, or status" arrow placement="top">
            <Box sx={{ flex: 1, minWidth: 240, maxWidth: 420, width: "100%" }}>
              <TableSearchInput
                searchQuery={searchTerm}
                onSearchChange={handleSearchChange}
                placeholder="Search by RM Name, Region, or Status"
                charCount={charCount}
              />
            </Box>
          </Tooltip>
          <Tooltip title="Open advanced filters" arrow placement="top">
            <Box>
              <ButtonTwo variant="outlined" startIcon={<Tune />}>
                Filter
              </ButtonTwo>
            </Box>
          </Tooltip>
        </Box>
      </Box>
      <Collapse in={expandedCards.rmList}>
        <CardContent sx={{ padding: 0 }}>
          <AppTable
            columns={tableColumns}
            rows={rmListData.results}
            rowKey="id"
            emptyMessage="No RMs found"
            hideMainScroll={false}
            count={rmListData.count}
            page={page}
            rowsPerPage={10}
            rowsPerPageOptions={[20, 50, 100]}
            onPageChange={handleChangePage}
            onRowsPerPageChange={() => {}}
          />
        </CardContent>
      </Collapse>

      {/* Alert at the bottom of the card */}
      {alertObj.active && (
        <Box sx={{ p: 2 }}>
          <Alert
            severity={alertObj.type}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={handleAlertClose}
              >
                <Close fontSize="inherit" />
              </IconButton>
            }
          >
            <AlertTitle>{alertObj.statusCode}</AlertTitle>
            {alertObj.msg}
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default RMMetricsDashboardTab;
